import { useContext, useEffect, useMemo, useState } from "react";
import BaseComponent from "common/Components/BaseComponent";
import Tabs from "common/Components/Tabs/CustomTabs";
import CustomSubTabs, { PlaceholderPanel } from "common/Components/Tabs/CustomSubTabs";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import { NodeView, CloudView } from "common/Constants/route-components";
import Tags from "pages/DataManagement/Tags";
import NodeContextImports from "NodeContextImport/NodeContextImports";

import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { EFFECTIVE_ACCESS_PAGE_KEY } from "common/Constants/EffectiveAccess.constants";

const getDataManagementSubTabComponent = (key, label) => {
  switch (key) {
    case "edgeHierarchy":
      return NodeView;
    case "cloudHierarchy":
      return CloudView;
    case "tags":
      return Tags;
    case "imports":
      return NodeContextImports;
    default:
      return () => <PlaceholderPanel label={label} />;
  }
};

const DataManagementContent = () => {
  const subTabKeys = INFRAMARK_CONSTANTS?.dataManagement?.subTabKeys ?? [];
  const subTabNames = INFRAMARK_CONSTANTS?.subTabNames ?? {};
  const tabs = useMemo(
    () =>
      subTabKeys.map((key) => {
        const label = subTabNames[key] ?? key;
        return {
          label,
          component: getDataManagementSubTabComponent(key, label),
        };
      }),
    [subTabKeys, subTabNames]
  );
  return <CustomSubTabs tabs={tabs} />;
};

const Administration = () => {
  const administration = INFRAMARK_CONSTANTS?.userRoutes?.userAdminRoutes;
  const dataManagement = INFRAMARK_CONSTANTS?.tabNames?.dataManagement;
  const { getCustomerName } = useContext(UserContext);
  const { filterTabsByAccess, canReturnToApplication } = useEffectiveAccess();
  const customerName = getCustomerName() ?? "";
  const [customerChangedSelected, setCustomerChangedSelected] = useState(false);
  const isCustomerSelected = customerName !== "" || customerChangedSelected;
  const accessiblePages = useMemo(
    () =>
      filterTabsByAccess([
        {
          label: dataManagement,
          component: DataManagementContent,
          hasSubTabs: true,
          pageKey: EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
        },
        ...(administration?.accessiblePages || []),
      ]),
    [filterTabsByAccess, administration?.accessiblePages, dataManagement]
  );
  const showHeaderButton = useMemo(
    () => ({
      ...administration?.headerButton,
      showButton: isCustomerSelected && canReturnToApplication(),
    }),
    [administration?.headerButton, isCustomerSelected, canReturnToApplication]
  );
  useEffect(() => {
    const handleCustomerChange = async (event) => {
      if (event?.detail?.groupName) {
        setCustomerChangedSelected(true);
      }
    };
    window.addEventListener("customerChanged", handleCustomerChange);
    return () =>
      window.removeEventListener("customerChanged", handleCustomerChange);
  }, []);
  return (
    <BaseComponent>
      <Tabs
        headerTitle={administration?.headerTitle}
        headerButton={showHeaderButton}
        tabs={accessiblePages}
      />
    </BaseComponent>
  );
};
export default Administration;
